import React from "react";
import "./SalaryTable.css";

const formatApproverRole = (role) => {
  if (role === "Согласующий по компании") return "Согл. компании";
  return role.replace("Согласующий", "Согл.");
};

const SalaryTable = ({
  selectedMonth,
  departments,
  onDepartmentClick,
  onDeleteApproval,
  deletingApprovalId,
  isAdms = false,
}) => {
  const formatMonthYear = (date) => {
    const months = [
      "Янв",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Июн",
      "Июл",
      "Авг",
      "Сен",
      "Окт",
      "Ноя",
      "Дек",
    ];
    const year = String(date.getFullYear()).slice(-2);
    return `${months[date.getMonth()]} ${year}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "На рассмотрении":
        return "⏳";
      case "Согласован":
        return "✓";
      case "Отклонено":
        return "✗";
      default:
        return "";
    }
  };

  const getStatusClassName = (status) => {
    switch (status) {
      case "На рассмотрении":
        return "status-pending";
      case "Согласован":
        return "status-approved";
      case "Отклонено":
        return "status-rejected";
      default:
        return "";
    }
  };

  const getRowClassName = (department) => {
    if (!department.approvers || department.approvers.length === 0) {
      return "";
    }

    const statuses = department.approvers.map((a) => a.status);
    const hasRejected = statuses.some((s) => s === "Отклонено");
    const hasPending = statuses.some((s) => s === "На рассмотрении");
    const allApproved = statuses.every((s) => s === "Согласован");

    if (hasRejected) {
      return "salary-table__row--rejected";
    }
    if (allApproved) {
      return "salary-table__row--approved";
    }
    if (hasPending) {
      return "salary-table__row--pending";
    }
    return "";
  };

  if (!departments || departments.length === 0) {
    return (
      <div className="salary-table__empty">
        <p>Нет данных для отображения</p>
      </div>
    );
  }

  return (
    <div className="salary-table__container">
      <table className="salary-table">
        <colgroup>
          <col className="salary-table__col-department" />
          <col className="salary-table__col-initiator" />
          <col className="salary-table__col-date" />
          <col className="salary-table__col-status" />
          <col className="salary-table__col-submission" />
          {onDeleteApproval && <col className="salary-table__col-actions" />}
        </colgroup>
        <thead>
          <tr>
            <th>Отдел</th>
            <th>Инициатор</th>
            <th>Период</th>
            <th>Статус</th>
            <th>Отправлено</th>
            {onDeleteApproval && <th>Действия</th>}
          </tr>
        </thead>
        <tbody>
          {departments.map((department) => (
            <tr
              key={department.approvalId ?? department.id}
              className={`salary-table__row ${getRowClassName(department)}`}
              onClick={() => onDepartmentClick && onDepartmentClick(department)}
              style={{ cursor: onDepartmentClick ? "pointer" : "default" }}
            >
              <td className="salary-table__department">{department.name}</td>
              <td className="salary-table__initiator">
                {department.initiator || "-"}
              </td>
              <td className="salary-table__date">
                {formatMonthYear(selectedMonth)}
              </td>
              <td className="salary-table__status">
                {department.approvers && department.approvers.length > 0 ? (
                  <div className="salary-table__status-list">
                    {department.approvers.map((approver, index) => (
                      <div
                        key={index}
                        className={`salary-table__approver ${getStatusClassName(
                          approver.status,
                        )}`}
                      >
                        <span className="salary-table__approver-role">
                          {formatApproverRole(approver.role)} ({approver.name})
                        </span>
                        <span
                          className={`salary-table__approver-status ${getStatusClassName(
                            approver.status,
                          )}`}
                        >
                          <span className="salary-table__status-icon">
                            {getStatusIcon(approver.status)}
                          </span>
                          {approver.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="salary-table__no-status">-</span>
                )}
              </td>
              <td className="salary-table__submission-date">
                {department.submissionDate
                  ? formatDate(department.submissionDate)
                  : "-"}
              </td>
              {onDeleteApproval && (() => {
                const canDelete = Boolean(department.canDelete);
                const isFullyApproved = Boolean(department.isFullyApproved);
                const isDeleting =
                  deletingApprovalId === department.approvalId;
                const showLockedHint =
                  !isAdms && isFullyApproved && !canDelete;

                return (
                  <td className="salary-table__actions">
                    <button
                      type="button"
                      className={`salary-table__delete-btn${
                        !canDelete ? " salary-table__delete-btn--disabled" : ""
                      }`}
                      disabled={!canDelete || isDeleting}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!canDelete) return;
                        onDeleteApproval(department);
                      }}
                      title={
                        showLockedHint
                          ? "Нельзя удалять согласованные согласования"
                          : canDelete
                            ? "Удалить согласование"
                            : "Удаление недоступно"
                      }
                    >
                      {isDeleting ? "…" : "Удалить"}
                    </button>
                    {showLockedHint && (
                      <span className="salary-table__delete-hint">
                        Нельзя удалять согласованные согласования
                      </span>
                    )}
                  </td>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SalaryTable;
